﻿import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useToast } from '@asad/crane-ui'

import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { doMfaSetup, doMfaEnable, doEnableEmailMfa } from '@/store/slices/auth-slice'

import { mapMfaError, downloadMfaBackupCodes } from '../mfa-utils'
import { useMfaEmailSetup } from './useMfaEmailSetup'
import { useClipboardFeedback } from './useClipboardFeedback'

type MfaSetupStep = 'method-select' | 'qr' | 'confirm' | 'backup' | 'email-otp'

export const useMfaSetup = () => {
  const [step, setStep] = React.useState<MfaSetupStep>('method-select')
  const [otpauthUrl, setOtpauthUrl] = React.useState('')
  const [secret, setSecret] = React.useState('')
  const [totpCode, setTotpCode] = React.useState('')
  const [backupCodes, setBackupCodes] = React.useState<string[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [setupError, setSetupError] = React.useState('')
  const [secretCopied, copySecretText] = useClipboardFeedback(2000)
  const isInitializingRef = React.useRef(false)
  const isSubmittingRef = React.useRef(false)

  const emailSetup = useMfaEmailSetup({
    onFirstSend: () => setStep('email-otp'),
  })

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const toast = useToast()

  const mfaPending = useAppSelector((state) => state.auth.mfaPending)
  const wasValidSetup = React.useRef(mfaPending.type === 'SETUP')

  const downloadBackupCodes = () => downloadMfaBackupCodes(backupCodes)

  const pickTotp = async () => {
    if (isInitializingRef.current) return
    isInitializingRef.current = true
    setStep('qr')
    setIsLoading(true)
    try {
      const res = await dispatch(doMfaSetup()).unwrap()
      setOtpauthUrl(res.otpauthUrl)
      setSecret(res.secret)
    } catch {
      navigate('/auth/login', { replace: true })
    } finally {
      setIsLoading(false)
      isInitializingRef.current = false
    }
  }

  const handleTotpEnableSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (totpCode.length !== 6 || isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsLoading(true)
    setSetupError('')
    try {
      const res = await dispatch(doMfaEnable(totpCode)).unwrap()
      setBackupCodes(res.backupCodes)
      setStep('backup')
    } catch (error) {
      setTotpCode('')
      setSetupError(mapMfaError(error, t, 'mfaSetup.invalidCode'))
    } finally {
      setIsLoading(false)
      isSubmittingRef.current = false
    }
  }

  const copySecret = () => copySecretText(secret)

  const handleEmailEnableSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (emailSetup.emailOtpCode.length !== 6 || isLoading) return
    setIsLoading(true)
    try {
      const res = await dispatch(
        doEnableEmailMfa({ emailMfaToken: emailSetup.setupEmailMfaToken, code: emailSetup.emailOtpCode }),
      ).unwrap()
      setBackupCodes(res.backupCodes)
      setStep('backup')
    } catch (error) {
      emailSetup.setEmailOtpCode('')
      emailSetup.setEmailError(mapMfaError(error, t))
    } finally {
      setIsLoading(false)
    }
  }

  const handleFinish = () => {
    toast.success(t('beNotifications.success.mfaEnabled'))
    navigate('/', { replace: true })
  }

  const goToConfirmFromQr = () => setStep('confirm')

  const backToQrFromConfirm = () => {
    setStep('qr')
    setTotpCode('')
  }

  const backToMethodSelectFromQr = () => {
    setStep('method-select')
    setOtpauthUrl('')
    setSecret('')
  }

  const backToMethodSelectFromEmailOtp = () => {
    setStep('method-select')
    emailSetup.reset()
  }

  React.useEffect(() => {
    if (!wasValidSetup.current) {
      navigate('/auth/login', { replace: true })
    }
  }, [navigate])

  return {
    step,
    otpauthUrl,
    secret,
    totpCode,
    setTotpCode,
    backupCodes,
    isLoading,
    secretCopied,
    emailOtpCode: emailSetup.emailOtpCode,
    setEmailOtpCode: emailSetup.setEmailOtpCode,
    emailSending: emailSetup.emailSending,
    emailSent: emailSetup.emailSent,
    emailError: emailSetup.emailError,
    resendSecondsLeft: emailSetup.resendSecondsLeft,
    setupError,
    pickTotp,
    handleTotpEnableSubmit,
    copySecret,
    pickEmail: emailSetup.pickEmail,
    resendSetupEmail: emailSetup.resendSetupEmail,
    handleEmailEnableSubmit,
    handleFinish,
    downloadBackupCodes,
    goToConfirmFromQr,
    backToQrFromConfirm,
    backToMethodSelectFromQr,
    backToMethodSelectFromEmailOtp,
  }
}
